import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuthStore } from "@/stores/auth.store";
import { teamSubscriptionService } from "@/services/team-subscription.service";
import { isActiveTeamSubscriber } from "@/utils/team-subscription.utils";
import { Icon } from "@/components/ui/icon"

type Props = {
  teamId: string;
  isTeamMember: boolean;
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);

export function GameTeamSubscriptionPromo({ teamId, isTeamMember }: Props) {
  const { user } = useAuthStore();

  const { data: subscription } = useQuery({
    queryKey: ["teamMySubscription", teamId, user?.id],
    queryFn: () => teamSubscriptionService.getMySubscription(teamId),
    enabled: !!user && !!teamId && isTeamMember,
    staleTime: 1000 * 60,
  });

  const { data: plans = [] } = useQuery({
    queryKey: ["teamSubscriptionPlansPublic", teamId],
    queryFn: () => teamSubscriptionService.listPlans(teamId),
    enabled: !!user && !!teamId && isTeamMember,
    staleTime: 1000 * 60,
  });

  const activePlans = plans.filter((plan) => plan.isActive);

  if (
    !isTeamMember ||
    isActiveTeamSubscriber(subscription) ||
    activePlans.length === 0
  ) {
    return null;
  }

  const planHint =
    activePlans.length === 1
      ? `${activePlans[0].name} · ${formatCurrency(activePlans[0].monthlyPrice)}/mês`
      : `${activePlans.length} pacotes a partir de ${formatCurrency(
          Math.min(...activePlans.map((p) => p.monthlyPrice)),
        )}/mês`;

  return (
    <div className="w-full rounded-xl border border-info/20 bg-info/10 p-4 dark:border-info/30 dark:bg-info/20">
      <div className="flex items-start gap-3 min-w-0">
        <div className="size-9 shrink-0 rounded-xl bg-info/15 flex items-center justify-center">
          <Icon name="workspace_premium" aria-hidden />
        </div>
        <div className="min-w-0 flex-1 flex flex-col gap-2 pt-0.5">
          <p className="text-sm leading-snug text-foreground">
            Assinatura VIP com acesso antecipado às listas.
          </p>
          <p className="text-xs text-muted-foreground">{planHint}</p>
          <Link
            to="/my-subscriptions"
            className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline w-fit"
          >
            Ver pacotes de assinatura
            <Icon name="arrow_forward" />
          </Link>
        </div>
      </div>
    </div>
  );
}
